import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchSkills,
  createSkill,
  deleteSkill,
  importSkillFromGitHub,
  fetchReviewQueue,
  reviewSkill,
  type Skill,
} from '@/admin/lib/api';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/admin/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/admin/components/ui/dialog';
import { Input } from '@/admin/components/ui/input';
import { Label } from '@/admin/components/ui/label';
import { Skeleton } from '@/admin/components/ui/skeleton';
import { Textarea } from '@/admin/components/ui/textarea';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/admin/components/ui/tabs';
import {
  IconPlus,
  IconTrash,
  IconChevronRight,
  IconSparkles,
  IconRefresh,
  IconBrandGithub,
  IconCheck,
  IconX,
} from '@tabler/icons-react';

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'published':
      return (
        <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-0 font-medium">
          Published
        </Badge>
      );
    case 'archived':
      return (
        <Badge variant="secondary" className="font-medium">
          Archived
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-muted-foreground font-medium">
          Draft
        </Badge>
      );
  }
}

function ReviewStatusBadge({ status }: { status?: string }) {
  switch (status) {
    case 'approved':
      return (
        <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-0 font-medium">
          Approved
        </Badge>
      );
    case 'rejected':
      return (
        <Badge className="bg-destructive/15 text-destructive border-0 font-medium">
          Rejected
        </Badge>
      );
    case 'pending_review':
      return (
        <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border-0 font-medium">
          Pending Review
        </Badge>
      );
    default:
      return null;
  }
}

function formatDate(dateStr?: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export const SkillsPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');

  // All skills state
  const [skills, setSkills] = useState<Skill[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Review queue state
  const [reviewQueue, setReviewQueue] = useState<Skill[]>([]);
  const [reviewTotal, setReviewTotal] = useState(0);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingSkill, setDeletingSkill] = useState<Skill | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Review dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingSkill, setRejectingSkill] = useState<Skill | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [reviewing, setReviewing] = useState<string | null>(null); // skill id being reviewed

  // Form states
  const [newDisplayName, setNewDisplayName] = useState('');

  // Import from GitHub states
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [githubUrl, setGithubUrl] = useState('');
  const [githubToken, setGithubToken] = useState(() => {
    return localStorage.getItem('github_import_token') || '';
  });

  const loadSkills = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchSkills(50, 0);
      setSkills(data.skills || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load skills');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadReviewQueue = useCallback(async () => {
    try {
      setReviewLoading(true);
      setReviewError(null);
      const data = await fetchReviewQueue(50, 0);
      setReviewQueue(data.skills || []);
      setReviewTotal(data.total || 0);
    } catch (err) {
      setReviewError(
        err instanceof Error ? err.message : 'Failed to load review queue'
      );
    } finally {
      setReviewLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSkills();
    loadReviewQueue();
  }, [loadSkills, loadReviewQueue]);

  const handleRefresh = () => {
    loadSkills();
    loadReviewQueue();
  };

  // Generate name from display name (lowercase, hyphenated)
  const generateName = (displayName: string): string => {
    return displayName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleCreateSkill = async () => {
    if (!newDisplayName.trim()) return;

    const name = generateName(newDisplayName);
    if (!name) {
      setCreateError('Display name must contain at least one letter or number');
      return;
    }

    setCreating(true);
    setCreateError(null);
    try {
      const created = await createSkill({
        name, // will be synced from SKILL.md
        display_name: newDisplayName.trim(),
        status: 'draft',
      });
      setSkills(prev => [created, ...prev]);
      setTotal(prev => prev + 1);
      setCreateDialogOpen(false);
      setNewDisplayName('');
      // Navigate to detail page
      navigate(`/skills/${created.id}`);
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : 'Failed to create skill'
      );
    } finally {
      setCreating(false);
    }
  };

  const handleOpenCreateDialog = (open: boolean) => {
    setCreateDialogOpen(open);
    if (open) {
      setCreateError(null);
    }
  };

  const handleImportFromGitHub = async () => {
    if (!githubUrl) return;

    setImporting(true);
    setImportError(null);
    try {
      const imported = await importSkillFromGitHub(
        githubUrl,
        githubToken || undefined
      );
      setSkills(prev => [imported, ...prev]);
      setTotal(prev => prev + 1);
      setImportDialogOpen(false);
      setGithubUrl('');
      // Keep token in localStorage for next time
      navigate(`/skills/${imported.id}`);
    } catch (err) {
      setImportError(
        err instanceof Error ? err.message : 'Failed to import skill'
      );
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, s: Skill) => {
    e.stopPropagation();
    setDeletingSkill(s);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingSkill) return;
    setDeleting(true);
    try {
      await deleteSkill(deletingSkill.id);
      setSkills(prev => prev.filter(s => s.id !== deletingSkill.id));
      setTotal(prev => prev - 1);
      // Sync review queue if deleted skill was pending community review
      if (deletingSkill.source === 'community') {
        setReviewQueue(prev => prev.filter(s => s.id !== deletingSkill.id));
        setReviewTotal(prev => Math.max(0, prev - (deletingSkill.review_status === 'pending_review' ? 1 : 0)));
      }
      setDeleteDialogOpen(false);
      setDeletingSkill(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete skill');
    } finally {
      setDeleting(false);
    }
  };

  const handleApprove = async (e: React.MouseEvent, s: Skill) => {
    e.stopPropagation();
    setReviewing(s.id);
    try {
      await reviewSkill(s.id, { status: 'approved' });
      setReviewQueue(prev => prev.filter(q => q.id !== s.id));
      setReviewTotal(prev => prev - 1);
      // Refresh all skills to show the newly published one
      loadSkills();
    } catch (err) {
      setReviewError(
        err instanceof Error ? err.message : 'Failed to approve skill'
      );
    } finally {
      setReviewing(null);
    }
  };

  const handleRejectClick = (e: React.MouseEvent, s: Skill) => {
    e.stopPropagation();
    setRejectingSkill(s);
    setRejectNote('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectingSkill) return;
    setReviewing(rejectingSkill.id);
    try {
      await reviewSkill(rejectingSkill.id, {
        status: 'rejected',
        note: rejectNote || undefined,
      });
      setReviewQueue(prev => prev.filter(q => q.id !== rejectingSkill.id));
      setReviewTotal(prev => prev - 1);
      setRejectDialogOpen(false);
      setRejectingSkill(null);
    } catch (err) {
      setReviewError(
        err instanceof Error ? err.message : 'Failed to reject skill'
      );
    } finally {
      setReviewing(null);
    }
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b bg-card">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <IconSparkles className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">Skills</h1>
            <p className="text-xs text-muted-foreground">{total} total</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={loading || reviewLoading}
          >
            <IconRefresh
              className={`h-4 w-4 ${loading || reviewLoading ? 'animate-spin' : ''}`}
            />
          </Button>
          <Button
            variant="outline"
            onClick={() => setImportDialogOpen(true)}
            size="sm"
          >
            <IconBrandGithub className="h-4 w-4 mr-1.5" />
            Import from GitHub
          </Button>
          <Button onClick={() => handleOpenCreateDialog(true)} size="sm">
            <IconPlus className="h-4 w-4 mr-1.5" />
            New Skill
          </Button>
        </div>
      </div>

      {/* Content with Tabs */}
      <div className="flex-1 overflow-y-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
          <div className="px-6 pt-4">
            <TabsList>
              <TabsTrigger value="all">All Skills</TabsTrigger>
              <TabsTrigger value="review" className="gap-1.5">
                Review Queue
                {reviewTotal > 0 && (
                  <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-xs">
                    {reviewTotal}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="all" className="flex-1 mt-0">
            {error && (
              <div className="mx-6 mt-4 p-3 bg-destructive/10 text-destructive text-sm rounded-lg border border-destructive/20">
                {error}
              </div>
            )}
            <div className="p-6 space-y-6">
              {/* Community Skills */}
              {(() => {
                const communitySkills = skills
                  .filter(s => s.source === 'community')
                  .sort((a, b) => {
                    const order: Record<string, number> = { pending_review: 0, rejected: 1, approved: 2 };
                    return (order[a.review_status ?? ''] ?? 3) - (order[b.review_status ?? ''] ?? 3);
                  });
                if (!loading && communitySkills.length === 0) return null;
                return (
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                      Community Skills
                      {!loading && <span className="ml-1.5 font-normal">({communitySkills.length})</span>}
                    </h3>
                    <div className="border rounded-lg bg-card overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30 hover:bg-muted/30">
                            <TableHead className="font-semibold">Skill</TableHead>
                            <TableHead className="font-semibold w-40">Submitter</TableHead>
                            <TableHead className="font-semibold w-32">Submitted</TableHead>
                            <TableHead className="font-semibold w-32">Review Status</TableHead>
                            <TableHead className="w-24"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {loading ? (
                            Array.from({ length: 2 }).map((_, i) => (
                              <TableRow key={i} className="h-16">
                                <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                                <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                                <TableCell><Skeleton className="h-8 w-16 ml-auto" /></TableCell>
                              </TableRow>
                            ))
                          ) : (
                            communitySkills.map(s => (
                              <TableRow
                                key={s.id}
                                className="cursor-pointer group h-16 transition-colors"
                                onClick={() => navigate(`/skills/${s.id}`)}
                              >
                                <TableCell>
                                  <div className="space-y-0.5">
                                    <div className="font-medium group-hover:text-primary transition-colors">
                                      {s.display_name || s.name}
                                    </div>
                                    <div className="text-xs text-muted-foreground font-mono">
                                      {s.name}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm">
                                  {s.submitter?.display_name || s.submitter?.email || '—'}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {formatDate(s.submitted_at)}
                                </TableCell>
                                <TableCell>
                                  <ReviewStatusBadge status={s.review_status} />
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                      onClick={e => handleDeleteClick(e, s)}
                                    >
                                      <IconTrash className="h-4 w-4" />
                                    </Button>
                                    <IconChevronRight className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                );
              })()}

              {/* Official Skills */}
              <div>
                {skills.some(s => s.source === 'community') && (
                  <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                    Official Skills
                    {!loading && <span className="ml-1.5 font-normal">({skills.filter(s => s.source !== 'community').length})</span>}
                  </h3>
                )}
                <div className="border rounded-lg bg-card overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30 hover:bg-muted/30">
                        <TableHead className="font-semibold">Skill</TableHead>
                        <TableHead className="font-semibold w-28">Category</TableHead>
                        <TableHead className="font-semibold w-20">Version</TableHead>
                        <TableHead className="font-semibold w-28">Status</TableHead>
                        <TableHead className="font-semibold w-20 text-center">
                          Featured
                        </TableHead>
                        <TableHead className="w-24"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        Array.from({ length: 5 }).map((_, i) => (
                          <TableRow key={i} className="h-16">
                            <TableCell>
                              <div className="space-y-2">
                                <Skeleton className="h-4 w-32" />
                                <Skeleton className="h-3 w-20" />
                              </div>
                            </TableCell>
                            <TableCell>
                              <Skeleton className="h-5 w-16" />
                            </TableCell>
                            <TableCell>
                              <Skeleton className="h-4 w-12" />
                            </TableCell>
                            <TableCell>
                              <Skeleton className="h-5 w-20" />
                            </TableCell>
                            <TableCell>
                              <Skeleton className="h-4 w-8 mx-auto" />
                            </TableCell>
                            <TableCell>
                              <Skeleton className="h-8 w-16 ml-auto" />
                            </TableCell>
                          </TableRow>
                        ))
                      ) : skills.filter(s => s.source !== 'community').length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="h-48">
                            <div className="text-center py-8">
                              <div className="w-14 h-14 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
                                <IconSparkles className="h-7 w-7 text-muted-foreground" />
                              </div>
                              <p className="text-sm font-medium mb-1">
                                No skills yet
                              </p>
                              <p className="text-xs text-muted-foreground mb-4">
                                Create your first skill to get started
                              </p>
                              <Button
                                onClick={() => handleOpenCreateDialog(true)}
                                size="sm"
                              >
                                <IconPlus className="h-4 w-4 mr-1.5" />
                                New Skill
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        skills.filter(s => s.source !== 'community').map(s => (
                          <TableRow
                            key={s.id}
                            className="cursor-pointer group h-16 transition-colors"
                            onClick={() => navigate(`/skills/${s.id}`)}
                          >
                            <TableCell>
                              <div className="space-y-0.5">
                                <div className="font-medium group-hover:text-primary transition-colors">
                                  {s.display_name || s.name}
                                </div>
                                <div className="text-xs text-muted-foreground font-mono">
                                  {s.name}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {s.category ? (
                                <Badge variant="outline" className="font-normal">
                                  {s.category}
                                </Badge>
                              ) : (
                                <span className="text-xs text-muted-foreground">
                                  —
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground font-mono">
                              {s.version}
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={s.status} />
                            </TableCell>
                            <TableCell className="text-center">
                              {s.featured ? (
                                <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border-0">
                                  ★
                                </Badge>
                              ) : (
                                <span className="text-xs text-muted-foreground">
                                  —
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                  onClick={e => handleDeleteClick(e, s)}
                                >
                                  <IconTrash className="h-4 w-4" />
                                </Button>
                                <IconChevronRight className="h-4 w-4 text-muted-foreground" />
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="review" className="flex-1 mt-0">
            {reviewError && (
              <div className="mx-6 mt-4 p-3 bg-destructive/10 text-destructive text-sm rounded-lg border border-destructive/20">
                {reviewError}
              </div>
            )}
            <div className="p-6">
              <div className="border rounded-lg bg-card overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <TableHead className="font-semibold">Skill</TableHead>
                      <TableHead className="font-semibold w-40">Submitter</TableHead>
                      <TableHead className="font-semibold w-32">Submitted</TableHead>
                      <TableHead className="font-semibold w-32">Status</TableHead>
                      <TableHead className="w-32"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reviewLoading ? (
                      Array.from({ length: 3 }).map((_, i) => (
                        <TableRow key={i} className="h-16">
                          <TableCell>
                            <div className="space-y-2">
                              <Skeleton className="h-4 w-32" />
                              <Skeleton className="h-3 w-20" />
                            </div>
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-4 w-24" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-4 w-20" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-5 w-24" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-8 w-24 ml-auto" />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : reviewQueue.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="h-48">
                          <div className="text-center py-8">
                            <div className="w-14 h-14 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
                              <IconCheck className="h-7 w-7 text-muted-foreground" />
                            </div>
                            <p className="text-sm font-medium mb-1">
                              All caught up
                            </p>
                            <p className="text-xs text-muted-foreground">
                              No skills pending review
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      reviewQueue.map(s => (
                        <TableRow
                          key={s.id}
                          className="cursor-pointer group h-16 transition-colors"
                          onClick={() => navigate(`/skills/${s.id}`)}
                        >
                          <TableCell>
                            <div className="space-y-0.5">
                              <div className="font-medium group-hover:text-primary transition-colors">
                                {s.display_name || s.name}
                              </div>
                              <div className="text-xs text-muted-foreground font-mono">
                                {s.name}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {s.submitter?.display_name || s.submitter?.email || '—'}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(s.submitted_at)}
                          </TableCell>
                          <TableCell>
                            <ReviewStatusBadge status={s.review_status} />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-500/10"
                                disabled={reviewing === s.id}
                                onClick={e => handleApprove(e, s)}
                              >
                                <IconCheck className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                disabled={reviewing === s.id}
                                onClick={e => handleRejectClick(e, s)}
                              >
                                <IconX className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={handleOpenCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Skill</DialogTitle>
            <DialogDescription>
              Enter a display name. A SKILL.md template will be auto-generated.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {createError && (
              <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-md border border-destructive/20">
                {createError}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="display-name">Display Name</Label>
              <Input
                id="display-name"
                value={newDisplayName}
                onChange={e => setNewDisplayName(e.target.value)}
                placeholder="My Awesome Skill"
                autoFocus
              />
            </div>
            {newDisplayName && (
              <div className="text-xs text-muted-foreground">
                <span className="font-medium">Name:</span>{' '}
                <code className="bg-muted px-1 py-0.5 rounded">
                  {generateName(newDisplayName) || '—'}
                </code>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => handleOpenCreateDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSkill}
              disabled={creating || !newDisplayName.trim()}
            >
              {creating ? 'Creating...' : 'Create Skill'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import from GitHub Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IconBrandGithub className="h-5 w-5" />
              Import from GitHub
            </DialogTitle>
            <DialogDescription>
              Import a skill from a GitHub repository. Paste the URL to the
              skill directory.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="github-url">GitHub URL</Label>
              <Input
                id="github-url"
                placeholder="https://github.com/owner/repo/tree/main/path/to/skill"
                value={githubUrl}
                onChange={e => setGithubUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Example:
                https://github.com/alirezarezvani/claude-skills/tree/main/engineering-team/senior-computer-vision
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="github-token">
                GitHub Token{' '}
                <span className="text-muted-foreground font-normal">
                  (optional)
                </span>
              </Label>
              <Input
                id="github-token"
                type="password"
                placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                value={githubToken}
                onChange={e => {
                  const value = e.target.value;
                  setGithubToken(value);
                  if (value) {
                    localStorage.setItem('github_import_token', value);
                  } else {
                    localStorage.removeItem('github_import_token');
                  }
                }}
              />
              <p className="text-xs text-muted-foreground">
                Personal access token for higher rate limits.{' '}
                <a
                  href="https://github.com/settings/tokens/new?description=Cueboard%20Skill%20Import&scopes=public_repo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Generate a new token
                </a>
              </p>
            </div>
            {importError && (
              <p className="text-sm text-destructive">{importError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setImportDialogOpen(false)}
              disabled={importing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImportFromGitHub}
              disabled={importing || !githubUrl}
            >
              {importing ? 'Importing...' : 'Import Skill'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete skill?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{' '}
              <span className="font-medium text-foreground">
                {deletingSkill?.name}
              </span>{' '}
              and all associated files. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Skill</DialogTitle>
            <DialogDescription>
              Reject{' '}
              <span className="font-medium text-foreground">
                {rejectingSkill?.display_name || rejectingSkill?.name}
              </span>
              . Optionally provide a reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="reject-note">Reason (optional)</Label>
              <Textarea
                id="reject-note"
                value={rejectNote}
                onChange={e => setRejectNote(e.target.value)}
                placeholder="Describe why this skill is being rejected..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => setRejectDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectConfirm}
              disabled={reviewing !== null}
            >
              {reviewing ? 'Rejecting...' : 'Reject Skill'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
